import WarehouseSelector, { useActiveWarehouse, WarehouseSelectionPopup } from 'src/components/atoms/WarehouseSelector';
import WarehouseRowEditor, { relocationHeaderStyles } from '../WarehouseRowEditor';
import { CustomField } from 'src/components/molecules/formik/CustomField';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AxiosRequestConfig } from 'axios';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ClearIcon from '@mui/icons-material/Clear';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import SaveIcon from '@mui/icons-material/Save';
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import ContentCardsContainer, { CardType, ContentCard } from 'src/components/organisms/ContentCard';
import { CustomDataGrid } from 'src/components/organisms/CustomDataGrid';
import useRequest from 'src/hooks/api/useRequest';
import { buildRelocationPayload, getRelocationLimit, useRelocateProductsTable } from 'src/hooks/table/configs/useRelocateProductsTable';
import type { RelocateProductRow } from 'src/hooks/table/configs/useRelocateProductsTable';
import { commitPendingTableEdits, discardTableEdits, getTableSubmitRows, hasTableChanges } from 'src/hooks/table/tableSettings/tableEditActions';
import { generateTablePageButtons } from 'src/utils/generateTablePageButtons';
import { relocateProductsFromDefault } from 'src/utils/apiRequests';
import { RelocationOrderPrintControls, useRelocationOrderPrinting } from './RelocateProducts';

type Identifier = number | string;
type Notice = { severity: 'success' | 'error' | 'info'; message: string };
const scannerFilterId = 'relocate-product-scan';
const editorFilterId = 'relocate-editor-product';

export const RelocateReceivedProducts = () => {
    const { warehouseId, selectWarehouse } = useActiveWarehouse();
    const printing = useRelocationOrderPrinting('relocateReceivedProducts');
    const [refreshKey, setRefreshKey] = useState(0);
    const [savedCount, setSavedCount] = useState(0);
    const changeWarehouse = (value: Identifier) => { setSavedCount(0); selectWarehouse(value); };

    if (warehouseId == null || warehouseId === '') return (
        <>
            {printing.feedback}
            <WarehouseSelectionPopup />
            <PageHeader {...{ title: 'Relocate Received Products', subTitle: 'Move products to another warehouse location', components: [{ component: <WarehouseSelector value={warehouseId} onChange={changeWarehouse} /> }] }} />
            <ContentCardsContainer><ContentCard {...{ cardId: 'RelocateProducts', cardTypes: [CardType.noHeader] }}><Typography>Select a warehouse to relocate products.</Typography></ContentCard></ContentCardsContainer>
            <Footer />
        </>
    );

    // Recreate the station after a successful save or warehouse change. Old drafts
    // and request results cannot be carried into a different warehouse or batch.
    return <>{printing.feedback}<RelocationStation key={`${warehouseId}:${refreshKey}`} warehouseId={warehouseId} refreshKey={refreshKey} savedCount={savedCount} onWarehouseChange={changeWarehouse} printing={printing} onSaved={(count) => { setSavedCount(count); setRefreshKey((value) => value + 1); }} /></>;
};

const RelocationStation = ({ warehouseId, refreshKey, savedCount, onWarehouseChange, onSaved, printing }: { warehouseId: Identifier; refreshKey: number; savedCount: number; onWarehouseChange: (value: Identifier) => void; onSaved: (count: number) => void; printing: ReturnType<typeof useRelocationOrderPrinting> }) => {
    const [job, setJob] = useState<{ id: number; request: AxiosRequestConfig; count: number } | null>(null);
    const [saving, setSaving] = useState(false);
    const [staging, setStaging] = useState(false);
    const stagingRef = useRef(false);
    const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
    const [rowValues, setRowValues] = useState({ Quantity: '', ToLocationId: '' });
    const [rowError, setRowError] = useState('');
    const quantityRef = useRef<HTMLInputElement>(null);
    const selectRowRef = useRef<((row: RelocateProductRow) => void) | null>(null);
    const onSelect = useCallback((row: RelocateProductRow) => selectRowRef.current?.(row), []);
    const [tableColumns, tableSettings, data] = useRelocateProductsTable(warehouseId, refreshKey, job != null || staging, onSelect);
    const columns = useMemo(() => tableColumns.map((column) => column.field === 'EANCode' ? { ...column, headerName: 'Barcode(EAN)' } : column), [tableColumns]);
    const [scannerValue, setScannerValue] = useState('');
    const [notice, setNotice] = useState<Notice | null>(() => savedCount ? { severity: 'success', message: `Relocated ${savedCount} product row${savedCount === 1 ? '' : 's'}.` } : null);
    const scannerRef = useRef<HTMLInputElement>(null);
    const savingRef = useRef(false);
    const jobSequence = useRef(0);
    const mountedRef = useRef(true);
    const latestSettingsRef = useRef(tableSettings);
    latestSettingsRef.current = tableSettings;
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

    const changes = hasTableChanges(tableSettings);
    const filterModel = tableSettings.filterModel ?? { items: [] };
    const quickFilterValues = filterModel.quickFilterValues ?? [];
    const quickFilterValue = quickFilterValues.join(' ');
    const selectedRow = data.rows.find(row => String(row.ProductId) === selectedRowId) ?? null;
    const available = selectedRow ? getRelocationLimit(selectedRow) : 0;
    const destinations = data.locations.filter(location => String(location.Id) !== String(data.receivingLocationId));
    const quantity = Number(rowValues.Quantity);
    const busy = saving || staging || tableSettings.loading;
    const canStage = !busy && selectedRow != null && Number.isFinite(quantity) && quantity > 0 && quantity <= available && destinations.some(location => String(location.Id) === rowValues.ToLocationId);
    const filteringSelectedProduct = selectedRow != null && filterModel.items.some(item => item.id === editorFilterId && String(item.value) === selectedRowId);
    const selectedHasEdits = selectedRow != null && tableSettings.changedRowsRef.current.some(row => String(row.ProductId) === selectedRowId);

    selectRowRef.current = row => {
        if (savingRef.current || stagingRef.current) return;
        const settings = latestSettingsRef.current;
        const live = settings.apiRef.current.getRow(settings.getRowId(row)) ?? row;
        setSelectedRowId(String(row.ProductId));
        setRowValues({ Quantity: String(Number(live.QuantityToRelocate) > 0 ? live.QuantityToRelocate : Math.min(1, getRelocationLimit(row))), ToLocationId: Number(live.RelocateToLocationId) > 0 ? String(live.RelocateToLocationId) : '' });
        setRowError('');
        requestAnimationFrame(() => { quantityRef.current?.focus(); quantityRef.current?.select(); });
    };

    const clearEditor = () => { setSelectedRowId(null); setRowValues({ Quantity: '', ToLocationId: '' }); setRowError(''); };
    const filterProduct = () => {
        if (!selectedRow || busy) return;
        const current = latestSettingsRef.current.filterModel ?? { items: [] };
        const items = current.items.filter(item => item.id !== editorFilterId);
        latestSettingsRef.current.apiRef.current.setFilterModel({ ...current, items: filteringSelectedProduct ? items : [...items, { id: editorFilterId, field: 'ProductId', operator: 'equals', value: selectedRow.ProductId }], quickFilterValues: [] });
    };
    const clearProductFilter = () => {
        const current = latestSettingsRef.current.filterModel ?? { items: [] };
        latestSettingsRef.current.apiRef.current.setFilterModel({ ...current, items: current.items.filter(item => item.id !== editorFilterId) });
    };
    const clearForm = () => {
        if (busy) return;
        clearEditor();
        clearProductFilter();
    };
    const stage = async () => {
        if (!canStage || stagingRef.current || savingRef.current || !selectedRow) return;
        stagingRef.current = true;
        setStaging(true);
        try {
            const editRows = latestSettingsRef.current.apiRef.current.state.editRows;
            if (Object.values(editRows).some(cells => Object.values(cells).some(cell => cell.error || cell.isProcessingProps))) throw new Error('Correct the invalid cell before staging changes.');
            await commitPendingTableEdits(latestSettingsRef.current);
            if (!mountedRef.current) return;
            const settings = latestSettingsRef.current;
            const live = settings.apiRef.current.getRow(settings.getRowId(selectedRow));
            if (!live || !settings.processRowUpdateRef.current) throw new Error('Reload the product before staging changes.');
            const next = { ...live, QuantityToRelocate: quantity, RelocateToLocationId: Number(rowValues.ToLocationId) };
            buildRelocationPayload([next], warehouseId, data.rows, data.locations);
            const committed = await settings.processRowUpdateRef.current(next, live);
            settings.apiRef.current.updateRows([committed]);
            setRowError('');
        } catch (error) {
            if (mountedRef.current) setRowError(error instanceof Error ? error.message : 'Could not stage this row.');
        } finally {
            stagingRef.current = false;
            if (mountedRef.current) setStaging(false);
        }
    };

    // Search identifiers even when their columns are hidden. Migrate a persisted
    // scanner filter from the old ProductId-only implementation to quick filtering.
    useEffect(() => {
        const legacyFilter = filterModel.items.find((item) => item.id === scannerFilterId);
        if (filterModel.quickFilterExcludeHiddenColumns === false && !legacyFilter) return;
        const legacyValue = String(legacyFilter?.value ?? '').trim();
        tableSettings.apiRef.current.setFilterModel({ ...filterModel, items: filterModel.items.filter((item) => item.id !== scannerFilterId), quickFilterValues: filterModel.quickFilterValues?.length ? filterModel.quickFilterValues : legacyValue ? [legacyValue] : [], quickFilterExcludeHiddenColumns: false });
    }, [filterModel, tableSettings.apiRef]);

    const clearScanFilter = () => {
        if (savingRef.current) return;
        const current = latestSettingsRef.current.filterModel ?? { items: [] };
        latestSettingsRef.current.apiRef.current.setFilterModel({ ...current, items: current.items.filter((item) => item.id !== scannerFilterId), quickFilterValues: [], quickFilterExcludeHiddenColumns: false });
        setScannerValue('');
        scannerRef.current?.focus();
    };

    const save = async () => {
        if (savingRef.current || stagingRef.current || tableSettings.loading) return;
        savingRef.current = true;
        setSaving(true);
        setNotice(null);
        try {
            const editRows = latestSettingsRef.current.apiRef.current.state.editRows;
            if (Object.values(editRows).some((cells) => Object.values(cells).some((cell) => cell.error || cell.isProcessingProps))) throw new Error('Correct the invalid cell before saving.');
            await commitPendingTableEdits(latestSettingsRef.current);
            if (!mountedRef.current) return;
            const editedRows = getTableSubmitRows(latestSettingsRef.current);
            const payload = buildRelocationPayload(editedRows, warehouseId, data.rows, data.locations);
            if (!payload.length) {
                savingRef.current = false;
                setSaving(false);
                setNotice({ severity: 'info', message: 'There are no changes to save.' });
                return;
            }
            setJob({ id: ++jobSequence.current, request: relocateProductsFromDefault(payload, warehouseId), count: payload.length });
        } catch (error) {
            if (!mountedRef.current) return;
            savingRef.current = false;
            setSaving(false);
            setNotice({ severity: 'error', message: error instanceof Error ? error.message : 'Could not prepare the relocation. Check the edited rows.' });
        }
    };

    const finishSave = (succeeded: boolean, response: unknown) => {
        if (!job) return;
        if (succeeded) { printing.onRelocated(response, warehouseId); onSaved(job.count); return; }
        setJob(null);
        savingRef.current = false;
        setSaving(false);
        setNotice({ severity: 'error', message: 'Relocation failed. Your edits have been kept.' });
    };

    const discard = () => {
        if (savingRef.current || stagingRef.current) return;
        discardTableEdits(latestSettingsRef.current);
        clearEditor();
        clearProductFilter();
        setNotice(null);
    };

    const changeWarehouse = (value: Identifier) => {
        if (savingRef.current || stagingRef.current || String(value) === String(warehouseId)) return;
        if (hasTableChanges(latestSettingsRef.current)) {
            setNotice({ severity: 'info', message: 'Save or discard your edits before changing warehouse.' });
            return;
        }
        onWarehouseChange(value);
    };

    const scan = (rawValue = scannerRef.current?.value ?? scannerValue) => {
        if (savingRef.current) return;
        const value = rawValue.trim();
        if (!value) return;
        setScannerValue('');
        if (value.toLowerCase() === 'cancel') { clearScanFilter(); return; }
        if (value.toLowerCase() === 'ok') { void save(); return; }
        const current = latestSettingsRef.current.filterModel ?? { items: [] };
        // Quick filter searches ProductId, ManufacturerProductId, EANCode and the
        // other searchable columns. Hidden, edited rows still belong to the batch.
        latestSettingsRef.current.apiRef.current.setFilterModel({ ...current, items: current.items.filter((item) => item.id !== scannerFilterId), quickFilterValues: [value], quickFilterExcludeHiddenColumns: false });
        setNotice(null);
        scannerRef.current?.focus();
    };

    const scanner = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField inputRef={scannerRef} value={scannerValue} disabled={saving} onChange={(event) => setScannerValue(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); scan((event.target as HTMLInputElement).value); } }} placeholder="Scan Product ID, Manufacturer ID, EAN, OK or Cancel" inputProps={{ 'aria-label': 'Scan product ID, manufacturer product ID, EAN, OK or Cancel' }} autoFocus sx={{ width: { xs: 180, sm: 300, md: 380 } }} />
            <CustomButton title="Scan" disabled={saving} size="small" iconButton={true} variant="outlined" icon={<ArrowForwardIcon />} onClick={() => scan()} />
        </Box>
    );

    return (
        <>
            {job && <RelocationRequest key={job.id} request={job.request} onComplete={finishSave} />}
            <Box sx={relocationHeaderStyles}>
                <PageHeader {...{
                    title: 'Relocate Received Products',
                    subTitle: 'Move products from the default receiving location to their warehouse locations',
                    components: [
                        { position: 'center', component: scanner },
                        { component: <WarehouseSelector value={warehouseId} disabled={saving || staging} onChange={changeWarehouse} /> },
                        { component: <CustomButton title="Discard" color="error" icon={<RestorePageIcon />} disabled={saving || staging || !changes} onClick={discard} /> },
                        { component: <CustomButton title="Save" icon={<SaveIcon />} isSubmitting={saving} disabled={busy || !changes} onClick={() => { void save(); }} /> },
                        { component: <RelocationOrderPrintControls printing={printing} disabled={saving} /> },
                        ...generateTablePageButtons(tableSettings, {hideQuickSearch: true}),
                    ],
                }} />
            </Box>
            {/* {notice && <Box sx={{ mx: 3, mb: 2 }}><Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.message}</Alert></Box>} */}
            {quickFilterValues.length > 0 && <Box sx={{ mx: 3, mb: 2 }}><Typography variant="body2">Quick filter: {quickFilterValue}. Save includes changes in all rows, including hidden rows.</Typography></Box>}
            <ContentCardsContainer>
                <WarehouseRowEditor config={{
                    id: 'Relocate received product', label: 'Relocation row', disabled: busy, onSubmit: stage, submitOnEnter: ['Quantity'],
                    summaries: [{ id: 'product', label: 'Selected product', title: selectedRow?.Product || 'Product', details: [{ id: 'ProductId', label: 'Product ID', value: selectedRow?.ProductId ?? '' }, { id: 'ManufacturerProductId', label: 'Manufacturer ID', value: selectedRow?.ManufacturerProductId ?? '' }] }],
                    actions: [<CustomButton key="filter" title={filteringSelectedProduct ? 'Show all products' : 'Filter this product'} icon={<FilterAltIcon />} variant={filteringSelectedProduct ? 'contained' : 'outlined'} disabled={!selectedRow || busy} onClick={filterProduct} />, <CustomButton key="clear" title="Clear" icon={<ClearIcon />} variant="text" disabled={busy || !selectedRow} onClick={clearForm} />],
                    layout: { columns: 'minmax(140px, 1fr) 100px minmax(180px, 1fr) 140px max-content', areas: '"source available destination quantity actions"', compactColumns: 'minmax(150px, 1fr) minmax(100px, 0.7fr) max-content', compactAreas: '"source available available" "destination quantity actions"' },
                    fields: [
                        { id: 'source', content: <CustomField options={{ name: 'FromLocation', label: 'From location', readOnly: true }} stateProps={{ state: data.receivingLocationName }} /> },
                        { id: 'available', content: <CustomField options={{ name: 'Available', label: 'Available', readOnly: true }} stateProps={{ state: selectedRow ? available : '' }} /> },
                        { id: 'destination', content: <CustomField options={{ name: 'ToLocationId', label: 'To location', select: true }} disabled={!selectedRow || busy || !destinations.length} stateProps={{ state: rowValues.ToLocationId, setState: value => setRowValues(current => ({ ...current, ToLocationId: value })) }}><MenuItem value="" disabled>Select a location</MenuItem>{destinations.map(location => <MenuItem key={location.Id} value={String(location.Id)}>{location.Label}</MenuItem>)}</CustomField> },
                        { id: 'quantity', content: <CustomField options={{ name: 'Quantity', label: 'Quantity to move', type: 'number' }} inputRef={quantityRef} disabled={!selectedRow || busy} stateProps={{ state: rowValues.Quantity, setState: value => setRowValues(current => ({ ...current, Quantity: value })) }} inputProps={{ min: 0, max: available, step: 'any' }} error={Boolean(rowError) || (rowValues.Quantity !== '' && (!Number.isFinite(quantity) || quantity <= 0 || quantity > available))} helperText={rowError || undefined} /> },
                        { id: 'actions', action: true, content: <CustomButton title={selectedHasEdits ? 'Replace staged changes' : 'Stage changes'} isSubmitting={staging} disabled={!canStage} onClick={() => void stage()} /> },
                    ],
                }} />
                <ContentCard {...{ cardId: 'RelocateProducts', md: 12, cardTypes: [CardType.noHeader], contentPadding: 0 }}>
                    <CustomDataGrid columns={columns} tableSettings={tableSettings} />
                </ContentCard>
            </ContentCardsContainer>
            <Footer />
        </>
    );
};

// Each attempt gets a fresh request hook, so a previous result cannot complete
// a retry. Completion is based on status, including success with an empty body.
const RelocationRequest = ({ request, onComplete }: { request: AxiosRequestConfig; onComplete: (succeeded: boolean, response: unknown) => void }) => {
    const [response, setRequest, , loading, status] = useRequest({ logLevel: ['error', 'success'] });
    const startRef = useRef(() => setRequest(request));
    const startedRef = useRef(false);
    const completeRef = useRef(onComplete);
    const handledRef = useRef(false);
    completeRef.current = onComplete;
    useEffect(() => { if (!startedRef.current) { startedRef.current = true; startRef.current(); } }, []);
    useEffect(() => {
        const state: unknown = status;
        if (handledRef.current || loading || state === 'idle' || state === 'loading') return;
        const succeeded = state === 'success' || (typeof state === 'number' && state >= 200 && state < 300);
        const failed = state === 'error' || state === 'aborted' || (typeof state === 'number' && state >= 400);
        if (!succeeded && !failed) return;
        handledRef.current = true;
        completeRef.current(succeeded, response);
    }, [response, loading, status]);
    return null;
};


export default RelocateReceivedProducts;

export { default as WarehouseFilter } from 'src/components/atoms/WarehouseSelector';
